(function () {
  'use strict';

  /* ===========================================================================
     Cutxi, prioridade de produção (versão corrigida)
     - Mantém a lógica original: detetar prioridade, adicionar a taxa correta,
       gravar atributos na encomenda.
     - Corrige o loop de sincronização que bloqueava o checkout:
         * sem MutationObserver no body inteiro
         * sem sincronizar em cliques genéricos nem em focus
         * sincroniza só quando o carrinho muda de facto, uma vez de cada vez
     - Defensivo: se uma taxa não puder ser aplicada (ex.: variante apagada),
       regista o aviso mas NUNCA bloqueia o checkout. Vender > não vender.
  =========================================================================== */

  var PRIORITY_VARIANTS = {
    normal_rapida: 57170989023576,
    normal_prioritaria: 57170989056344,
    normal_urgente: 57170989089112,

    batizado_rapida: 57170989154648,
    batizado_prioritaria: 57170989121880,
    batizado_urgente: 57170989187416
  };

  var PRIORITY_VARIANT_IDS = Object.values(PRIORITY_VARIANTS).map(String);

  var LEVEL_PRIORITY = {
    rapida: 1,
    prioritaria: 2,
    urgente: 3
  };

  var GROUP_PRIORITY = {
    normal: 1,
    batizado: 2
  };

  // Tempo máximo (ms) que o checkout espera pela sincronização final.
  // Passado este tempo, avança na mesma. Garante que nunca fica preso.
  var CHECKOUT_SYNC_TIMEOUT = 3000;

  // Espera (ms) para agrupar várias mudanças seguidas numa só sincronização.
  var SYNC_DEBOUNCE = 350;

  /* ---- estado interno -------------------------------------------------- */
  var isSyncing = false;       // true enquanto o próprio script mexe no carrinho
  var pendingSync = false;     // houve mudança externa enquanto sincronizava?
  var checkoutInProgress = false;
  var debounceTimer = null;

  /* ---- helpers de deteção (iguais à versão original) ------------------- */

  function cartRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function normalize(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  function isPriorityFeeItem(item) {
    return PRIORITY_VARIANT_IDS.includes(String(item.variant_id));
  }

  function detectLevelFromText(text) {
    var value = normalize(text);
    if (!value || value.includes('sem prioridade') || value.includes('sem urgencia')) {
      return '';
    }
    if (value.includes('urgente')) return 'urgente';
    if (value.includes('expresso') || value.includes('rapida') || value.includes('rapido')) return 'rapida';
    if (value.includes('prioritario') || value.includes('prioritaria')) return 'prioritaria';
    return '';
  }

  function detectGroupFromItem(item) {
    var properties = item.properties || {};
    var hiddenGroup = normalize(properties._urgency_group);
    if (hiddenGroup === 'batizado') return 'batizado';

    var propertyText = Object.keys(properties)
      .map(function (key) { return key + ' ' + properties[key]; })
      .join(' ');

    var combinedText = normalize([
      normalize(item.product_type),
      normalize(item.product_title || item.title),
      normalize(item.handle),
      propertyText
    ].join(' '));

    if (
      combinedText.includes('batizado') ||
      combinedText.includes('baptizado') ||
      combinedText.includes('comunhao') ||
      combinedText.includes('comunhão') ||
      combinedText.includes('15 a 20') ||
      combinedText.includes('prazo20')
    ) {
      return 'batizado';
    }
    return 'normal';
  }

  function detectLevelFromProperties(properties) {
    if (!properties) return '';
    var hiddenLevel = normalize(properties._urgency_level);
    if (LEVEL_PRIORITY[hiddenLevel]) return hiddenLevel;

    var values = Object.keys(properties)
      .map(function (key) { return key + ' ' + properties[key]; })
      .join(' ');
    return detectLevelFromText(values);
  }

  function resolveCartPriority(cart) {
    var realItems = cart.items.filter(function (item) {
      return !isPriorityFeeItem(item);
    });

    var finalGroup = 'normal';
    var finalLevel = '';
    var maxLevelPriority = 0;

    realItems.forEach(function (item) {
      var properties = item.properties || {};
      var itemGroup = detectGroupFromItem(item);
      if (GROUP_PRIORITY[itemGroup] > GROUP_PRIORITY[finalGroup]) {
        finalGroup = itemGroup;
      }
      var itemLevel = detectLevelFromProperties(properties);
      if (LEVEL_PRIORITY[itemLevel] && LEVEL_PRIORITY[itemLevel] > maxLevelPriority) {
        finalLevel = itemLevel;
        maxLevelPriority = LEVEL_PRIORITY[itemLevel];
      }
    });

    if (!finalLevel) return null;

    return {
      group: finalGroup,
      level: finalLevel,
      key: finalGroup + '_' + finalLevel,
      variantId: PRIORITY_VARIANTS[finalGroup + '_' + finalLevel]
    };
  }

  /* ---- chamadas ao carrinho ------------------------------------------- */

  function fetchCart() {
    return fetch(cartRoot() + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function addPriorityFee(resolution) {
    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{
          id: resolution.variantId,
          quantity: 1,
          properties: {
            _cutxi_fee_type: 'production_priority',
            _cutxi_fee_key: resolution.key
          }
        }]
      })
    });
  }

  function changeLine(lineKey, quantity) {
    return fetch(cartRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    });
  }

  function updateAttributes(attributes) {
    return fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: attributes })
    });
  }

  function removeAllPriorityFees(cart) {
    var feeLines = cart.items.filter(isPriorityFeeItem);
    var chain = Promise.resolve();
    feeLines.forEach(function (line) {
      chain = chain.then(function () { return changeLine(line.key, 0); });
    });
    return chain;
  }

  /* ---- sincronização principal ---------------------------------------- */

  async function doSync() {
    var cart = await fetchCart();

    var realItems = cart.items.filter(function (item) {
      return !isPriorityFeeItem(item);
    });

    // Carrinho sem itens reais (só taxas) ou sem prioridade escolhida:
    // limpar taxas e atributos.
    var resolution = realItems.length === 0 ? null : resolveCartPriority(cart);

    if (!resolution) {
      await removeAllPriorityFees(cart);
      await updateAttributes({
        cutxi_priority_group: '',
        cutxi_priority_level: '',
        cutxi_priority_key: ''
      });
      return;
    }

    var desiredVariantId = String(resolution.variantId);

    // Remover taxas que não correspondam à desejada.
    var feeLines = cart.items.filter(isPriorityFeeItem);
    for (var i = 0; i < feeLines.length; i++) {
      if (String(feeLines[i].variant_id) !== desiredVariantId) {
        await changeLine(feeLines[i].key, 0);
      }
    }

    // Reler o carrinho depois das remoções.
    cart = await fetchCart();
    feeLines = cart.items.filter(isPriorityFeeItem);
    var correctFeeLines = feeLines.filter(function (line) {
      return String(line.variant_id) === desiredVariantId;
    });

    if (correctFeeLines.length === 0) {
      // Adicionar a taxa. DEFENSIVO: se falhar (ex.: variante apagada),
      // não rebenta, regista aviso e segue.
      var resp = await addPriorityFee(resolution);
      if (!resp || !resp.ok) {
        console.warn('Cutxi prioridade: não foi possível adicionar a taxa', resolution.key,
          '(variante', desiredVariantId, '). O checkout continua na mesma.');
        return;
      }
    } else {
      if (correctFeeLines[0].quantity !== 1) {
        await changeLine(correctFeeLines[0].key, 1);
      }
      for (var j = 1; j < correctFeeLines.length; j++) {
        await changeLine(correctFeeLines[j].key, 0);
      }
    }

    await updateAttributes({
      cutxi_priority_group: resolution.group,
      cutxi_priority_level: resolution.level,
      cutxi_priority_key: resolution.key
    });

    document.dispatchEvent(new CustomEvent('cutxi:production-priority-synced'));
  }

  // Wrapper com guarda: nunca corre duas sincronizações em paralelo.
  // Se chega um pedido enquanto sincroniza, marca pendingSync e repete no fim.
  async function syncProductionPriority() {
    if (isSyncing) {
      pendingSync = true;
      return;
    }
    isSyncing = true;
    try {
      await doSync();
    } catch (error) {
      console.warn('Cutxi prioridade: erro ao sincronizar', error);
    } finally {
      isSyncing = false;
      if (pendingSync) {
        pendingSync = false;
        // pequeno intervalo para não encavalitar
        setTimeout(syncProductionPriority, 50);
      }
    }
  }

  // Agendamento com debounce. Várias mudanças seguidas = uma só sync.
  function scheduleSync() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(syncProductionPriority, SYNC_DEBOUNCE);
  }

  /* ---- deteção de mudanças no carrinho (sem loop) ---------------------
     Em vez de vigiar o DOM inteiro, ouvimos os eventos de carrinho do tema.
     Como as chamadas do próprio script correm com isSyncing=true, elas não
     re-disparam sync (o guard trata disso). Isto elimina o ciclo infinito.
  --------------------------------------------------------------------- */

  function bindCartEvents() {
    var events = [
      'cart:updated',
      'cart:refresh',
      'ajaxCart:updated',
      'cart:build',
      'cart:change'
    ];
    events.forEach(function (name) {
      document.addEventListener(name, function () {
        if (!isSyncing) scheduleSync();
      });
    });

    // Quando a caixa de prioridade muda na página de produto.
    document.body.addEventListener('change', function (event) {
      if (event.target && event.target.matches &&
          event.target.matches('input[name="properties[_urgency_level]"]')) {
        // a mudança só afeta o carrinho depois de adicionar ao carrinho;
        // não sincronizamos aqui para evitar trabalho desnecessário.
      }
    });
  }

  /* ---- sincronização final antes do checkout -------------------------- */

  function syncBeforeCheckout() {
    // Corre uma sincronização e resolve quando terminar, OU ao fim do
    // timeout de segurança (o que vier primeiro). Nunca fica preso.
    return new Promise(function (resolve) {
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        resolve();
      }
      setTimeout(finish, CHECKOUT_SYNC_TIMEOUT);
      Promise.resolve(syncProductionPriority()).then(finish).catch(finish);
    });
  }

  /* ---- interceção do botão de checkout -------------------------------- */

  document.addEventListener('click', function (event) {
    var checkoutButton = event.target.closest(
      'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], ' +
      '[data-checkout-button], .cart__checkout, .cart__checkout-button'
    );
    if (!checkoutButton) return;
    if (checkoutInProgress) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutInProgress = true;

    var originalText = checkoutButton.innerText || checkoutButton.value || '';
    if (checkoutButton.tagName === 'BUTTON' || checkoutButton.tagName === 'INPUT') {
      checkoutButton.disabled = true;
    }
    if (checkoutButton.innerText) checkoutButton.innerText = 'A preparar checkout...';
    if (checkoutButton.value) checkoutButton.value = 'A preparar checkout...';

    syncBeforeCheckout().then(function () {
      // Avança sempre para o checkout de forma fiável.
      // (window.location é mais robusto que form.submit, que não envia o name="checkout")
      window.location.href = cartRoot() + 'checkout';
    });
  }, true);

  /* ---- arranque -------------------------------------------------------- */

  window.CutxiProductionPriority = { sync: syncProductionPriority };

  function init() {
    bindCartEvents();
    // uma sincronização inicial, para o caso de o carrinho já ter itens.
    syncProductionPriority();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();